import { message, Modal, Spin } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useExistingLead } from 'src/app/hooks/useGetExistingLead';
import { Card, Tabs, Input, Button } from 'antd';
import HeadingTypography from '../../shared/Typography/Heading';
import { BaseLayoutComponent, DocumentsTab, DocumentsUpload, GuarantorsTab, ProductDetailsTab } from '../../shared';
import { Content } from 'antd/es/layout/layout';
import TextTypography from '../../shared/Typography/Text';
import {
    checkLeadGurantorsDataForErrors,
    checkLeadInforDataForErrors,
    checkLeadProductsDataForErrors,
    formateDbDocuments,
    formateDbGurantors,
    formateDbProducts,
    getFutureDate,
    getUpdatedAllCombinedData,
    IDocument,
    IGuarantor,
    ILeadEntity,
    ILeadValues,
    IProduct, UploadedFile,
} from '@rasayi-workspace/shared';
import EditIcon from 'src/assets/icons/editIcone';
import { TabsProps } from 'antd/lib';
import { useMutation } from '@tanstack/react-query';
import { useLeadContext } from '@contexts';
import { updateLead } from 'src/app/services/lead';
import useGetLeadsBasicData from 'src/app/hooks/useGetLeadsBasicData';
import GuarantorsDetails from './guarantorsData';
import JSZip from 'jszip';
import { saveAs } from 'file-saver'
import { getFileFromFilename } from '../../../services/lead';
import LeadInfoTab from '../../shared/LeadsTable/addButton/LeadModalTabs/LeadInfoTab';


const LeadDetails = () => {
    const { id } = useParams();
    const { fetchExistingLead, isLoading, data } = useExistingLead(id ?? '', false);
    useGetLeadsBasicData();

    const [activeTab, setActiveTab] = useState<string>('1');
    const { setUpdatingLeadEntity, updatingLeadEntity } = useLeadContext();
    const resetDocuments = useRef(false);
    const [error, setError] = useState<{ type: 'error' | 'success' | 'info' | 'warning' | ''; message: string }>({ type: '', message: '' });
    const [leadInfo, setLeadInfo] = useState<ILeadValues>({
        user_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        secondary_phone: '',
        cnic_number: '',
        dob: '',
        gender: '',
        lead_status: '',
        province: '',
        city: '',
        address: '',
        secondary_address: '',
        organization: '',
        segment: '',
        channel_id: '',
        comments: '',
    });
    const [leadDocuments, setLeadDocuments] = useState<IDocument>();
    const [leadProducts, setLeadProducts] = useState<IProduct[]>([]);
    const [leadGurantor, setLeadGurantor] = useState<IGuarantor[]>([]);
    const setLeaddata = (item: ILeadEntity) => {        
        setUpdatingLeadEntity(item);
        setLeadInfo({
            user_id: item.user_id,
            first_name: item.first_name,
            last_name: item.last_name,
            email: item.email,
            phone: item.phone,
            secondary_phone: item.secondary_phone,
            cnic_number: item.cnic_number,
            dob: item.dob,
            gender: item.gender,
            lead_status: item.lead_status,
            province: item.province,
            city: item.city,
            address: item.address,
            secondary_address: item.secondary_address,
            organization: item.organization,
            segment: item.segment,
            channel_id: item.channel_id,
            comments: '',
        });
        const products: IProduct[] = formateDbProducts(item.products);
        setLeadProducts(products);
        
        const documents: IDocument = formateDbDocuments(item.customer?.documents||[]);

        setLeadDocuments(documents);

        const gurantors: IGuarantor[] = formateDbGurantors(item.guarantors);
        setLeadGurantor(gurantors);
    };

    const [leadModalVisible, setLeadModalVisible] = useState(false);
    const [comment, setComment] = useState('');
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        fetchExistingLead();
    }, []);

    useEffect(() => {
        if (data) {
            setLeaddata(data);
        }
    }, [data]);

    const leadInfoData = useMemo(() => {
        return {
            'User Id': data?.id?.slice(-9) ?? 'N/A',
            Agent: (data?.user?.first_name ?? '') + ' ' + (data?.user?.last_name ?? '') || 'N/A',
            'First name': data?.first_name ?? 'N/A',
            'Last name': data?.last_name ?? 'N/A',
            Email: data?.email ?? 'N/A',
            'Phone Number': data?.phone ?? 'N/A',
            'Secondary phone': data?.secondary_phone ?? 'N/A',
            CNIC: data?.cnic_number ?? 'N/A',
            DOB: data?.dob ?? 'N/A',
            Gender: data?.gender ?? 'N/A',
            Status: data?.lead_status ?? 'N/A',
            Province: data?.province ?? 'N/A',
            City: data?.city ?? 'N/A',
            Address: data?.address ?? 'N/A',
            'Secondary Address': data?.secondary_address ?? 'N/A',
            Organization: data?.organization ?? 'N/A',
            Segment: data?.segment ?? 'N/A',
            Channel: data?.channel?.name ?? 'N/A',
        };
    }, [data]);

    const productDetails = useMemo(() => {
        return (
            data?.products?.map((product, index) => {
                let lastInstallment = '';
                if (product?.instalment_plan?.cycle) lastInstallment = getFutureDate(product?.instalment_plan?.cycle);

                let totalPrice = 0;
                let montlyInstallment = 0;
                const retail_price = Number(product.retail_price) ?? 0;
                const selectedInstallment = product.instalment_plan;
                if (selectedInstallment?.cycle) {
                    const aprPercentage = (product.apr ?? 0) * (selectedInstallment.cycle ?? 0);

                    const aprAmount = retail_price * (aprPercentage / 100);

                    let totalLoanAmount = Number(aprAmount + retail_price);

                    if (product.service_charges) {
                        totalLoanAmount += 400;
                    }
                    if (product.delivery_charges) {
                        totalLoanAmount += 600;
                    }
                    if (product.locking_charges) {
                        totalLoanAmount += Number(product.locking_charges) ?? 0;
                    }
                    montlyInstallment = totalLoanAmount / selectedInstallment.cycle;

                    totalPrice = totalLoanAmount;
                    if (Number(product.down_payment)) {
                        totalPrice += Number(product.down_payment);
                    }
                    if (Number(product.token_amount)) {
                        totalPrice += Number(product.token_amount);
                    }
                }
                return {
                    'Product Type': product?.product_type?.name ?? 'N/A',
                    Quantity: product?.quantity?.toString() ?? 'N/A',
                    Brand: product?.brand?.name ?? 'N/A',
                    'Product Name': product?.product_name ?? 'N/A',
                    'Product Description': product?.product_description ?? 'N/A',
                    'Looking Charges': product?.locking_charges?.toString() ?? 'N/A',
                    'Delivery Charges': product?.delivery_charges?.toString() ?? 'N/A',
                    'Service Charges': product?.service_charges?.toString() ?? 'N/A',
                    'Down Payment': product?.down_payment?.toString() ?? 'N/A',
                    'Token Amount': product?.token_amount?.toString() ?? 'N/A',
                    Apr: product?.apr?.toString() ?? 'N/A',
                    'Retail Proce': product?.retail_price?.toString() ?? 'N/A',
                    'Installment Plan': product?.instalment_plan?.cycle ? product?.instalment_plan?.cycle + ' months' : 'N/A',
                    'Monthly Installment': montlyInstallment.toFixed(2).toString(),
                    'Last Installment': lastInstallment,
                    'Toral Price': totalPrice.toString(),
                };
            }) ?? []
        );
    }, [data]);

    const comments = useMemo(() => {
        return data?.comments?.map((comment, index) => {
            return {
                key: (index + 1).toString(),
                name: (comment.user?.first_name ?? '') + ' ' + (comment.user?.last_name ?? '') || 'N/A',
                text: comment.text,
            };
        });
    }, [data]);

    const documents = useMemo(() => {
        return formateDbDocuments(data?.customer?.documents ?? []);
    }, [data]);

    const resetForm = () => {
        setLeadInfo({
            user_id: '',
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            secondary_phone: '',
            cnic_number: '',
            dob: '',
            gender: '',
            lead_status: '',
            province: '',
            city: '',
            address: '',
            secondary_address: '',
            organization: '',
            segment: '',
            channel_id: '',
            comments: '',
        });
        setLeadDocuments({
            profile_pic: [],
            cnic: [],
            bank_statment: [],
            bills: [],
            salary_slip: [],
            cheque: [],
            other: [],
        });
        setLeadGurantor([]);
        setLeadProducts([]);
    };

    const onCloseHandler = () => {
        setUpdatingLeadEntity(null);
        setLeadModalVisible(false);
        resetForm();
    };

    const handleTabChange = (key: string) => {
        let dataError = '';
        if (activeTab === '1') dataError = checkLeadInforDataForErrors(leadInfo);
        if (activeTab === '2') dataError = checkLeadProductsDataForErrors(leadProducts);
        if (activeTab === '4') dataError = checkLeadGurantorsDataForErrors(leadGurantor);

        if (!dataError) setActiveTab(key);

        setError({ type: dataError ? 'error' : '', message: dataError });
    };

    const { mutate: updateLeadHandler, isLoading: isUpdateLoading } = useMutation<any, unknown, { isOnlyComment: boolean }>({
        mutationKey: ['updateLead'],
        mutationFn: async props => {
            if (props.isOnlyComment) {
                const formData = new FormData();
                const user = JSON.parse(localStorage.getItem('rasayi-auth-user') || '');

                formData.append('comment', comment);
                return updateLead(formData, id ?? '');
            } else {
                const formData = getUpdatedAllCombinedData(updatingLeadEntity, leadInfo, leadProducts, leadGurantor, leadDocuments);
                onCloseHandler();
                return updateLead(formData, id ?? '');
            }
        },
        onSuccess: () => {
            setComment('');
            fetchExistingLead();
            setLeadModalVisible(false);
            resetForm();
            messageApi.success('Lead updated successfully!');
        },
        onError: (e: any) => {
            messageApi.error(
                <div>
                    {e?.response?.data?.message?.map((msg: string, index: number) => (
                        <div className="text-start	" key={index}>
                            {msg}
                        </div>
                    )) || 'Failed to update lead. Contact support.'}
                </div>
            );
        },
    });

    const submitData = () => {
        let dataError = '';
        if (activeTab === '1') dataError = checkLeadInforDataForErrors(leadInfo);
        if (activeTab === '2') dataError = checkLeadProductsDataForErrors(leadProducts);
        if (activeTab === '4') dataError = checkLeadGurantorsDataForErrors(leadGurantor);

        setError({ type: dataError ? 'error' : '', message: dataError });

        if (!dataError) {
            updateLeadHandler({ isOnlyComment: false });
        }
    };

    const tabItems: TabsProps['items'] = [
        {
            key: '1',
            label: 'Lead Info',
            children: (
                <LeadInfoTab onSubmit={submitData} saveData={setLeadInfo} initialValue={leadInfo} onClose={onCloseHandler} isLoading={isLoading || isUpdateLoading} error={error} setError={setError} />
            ),
        },
        {
            key: '2',
            label: 'Product Details',
            children: (
                <ProductDetailsTab
                    onClose={onCloseHandler}
                    onSaveProductValues={submitData}
                    saveData={setLeadProducts}
                    initialValue={leadProducts}
                    isLoading={isLoading || isUpdateLoading}
                    exsistingProductLength={updatingLeadEntity?.products?.length ?? 0}
                    error={error}
                    setError={setError}
                />
            ),
        },
        {
            key: '3',
            label: 'Documents',
            children: (
                <DocumentsTab
                    onClose={onCloseHandler}
                    onSaveDocumenst={submitData}
                    saveData={setLeadDocuments}
                    initialValue={leadDocuments}
                    isLoading={isLoading || isUpdateLoading}
                    leadId={id ?? ''}
                    resetDocuments={resetDocuments}
                />
            ),
        },
        {
            key: '4',
            label: 'Guarantors',
            children: (
                <GuarantorsTab
                    onClose={onCloseHandler}
                    onSaveGuarantor={submitData}
                    saveData={setLeadGurantor}
                    initialValue={leadGurantor}
                    isLoading={isLoading || isUpdateLoading}
                    exsistingProductLength={updatingLeadEntity?.guarantors?.length ?? 0}
                    error={error}
                    setError={setError}
                    leadId={id ?? ''}
                />
            ),
        },
    ];

  const downloadAllDocuments = async () => {
    const zip = new JSZip();

    const addDocumentsToZip = async (files: UploadedFile[], category: string) => {
      const filePromises = files.map(async (file) => {
        try {

          const preSignedUrl = await getFileFromFilename(file.url || '');

          const response = await fetch(preSignedUrl);

          const blob = await response.blob();

          const fileName = file.file?.name || `${category}_${file.uid}.png`;

          zip.file(fileName, blob);
        } catch (error) {
          console.error(`Failed to fetch document: ${file.url}`, error);
        }
      });

      await Promise.all(filePromises);
    };

    // Add documents from each category to the ZIP
    await addDocumentsToZip(documents.profile_pic, 'profile_pic');
    await addDocumentsToZip(documents.cnic, 'cnic');
    await addDocumentsToZip(documents.bank_statment, 'bank_statement');
    await addDocumentsToZip(documents.bills, 'bills');
    await addDocumentsToZip(documents.salary_slip, 'salary_slip');
    await addDocumentsToZip(documents.cheque, 'cheque');
    await addDocumentsToZip(documents.other, 'other');

    // After adding all documents, generate the ZIP file and trigger the download
    zip.generateAsync({ type: 'blob' }).then(content => {
      saveAs(content, 'all_documents.zip');
    });
  };


  if(!id) return <></>


  if (isLoading) {
        return (
            <div className="spinner-container flex justify-center p-[20px]">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <>
            <BaseLayoutComponent
                children={
                    <Content className="m-5 bg">
                        <div className="flex justify-between">
                            {contextHolder}
                            <HeadingTypography fontSize={24}>View Lead Details</HeadingTypography>
                            <div
                                onClick={() => {
                                    setLeadModalVisible(true);
                                }}
                                className="text-[#007AFF] cursor-pointer"
                            >
                                Edit Lead Details <EditIcon />
                            </div>
                        </div>
                        <Card title="Lead Info" className="mb-4 mt-5">
                            <div className="grid grid-cols-2 gap-4">
                                {Object.entries(leadInfoData).map(([key, value], index) => (
                                    <DataRow key={key + index} title={key} value={value} index={index} />
                                ))}
                            </div>
                        </Card>

                        {/* Product Details */}
                        <Card title="Product Details" className="mb-4">
                            <Tabs>
                                {productDetails.map((product, index) => (
                                    <Tabs.TabPane tab={`Product ${index + 1}`} key={`Product ${index + 1}`}>
                                        <div className="grid grid-cols-2 gap-4">
                                            <>
                                                {Object.entries(product).map(([key, value], index) => (
                                                    <DataRow key={key + index} title={key} value={value} index={index} />
                                                ))}
                                            </>
                                        </div>
                                    </Tabs.TabPane>
                                ))}
                            </Tabs>
                        </Card>

                        <div className="flex">
                            {/* Guarantors */}
                            <Card title="Guarantors" className="mb-4 w-[50%] ">
                                <div className=" flex flex-col gap-[20px]">
                                    <GuarantorsDetails guarantors={leadGurantor} leadId={id} fetchExistingLead={fetchExistingLead}/>
                                </div>
                            </Card>

                            {/* Comments */}
                            <Card title="Comments" className="mb-4 w-[50%]">
                                <div className="flex flex-col gap-[10px]">
                                    {comments?.map(comment => (
                                        <div className="flex flex-col justify-start items-start gap-[10px] p-3 border rounded-lg bg-[#FAFAFA]">
                                            <HeadingTypography fontSize={14}>{comment.name}</HeadingTypography>
                                            <TextTypography fontSize={14}>{comment.text}</TextTypography>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-5 flex gap-[10px]">
                                    <Input.TextArea
                                        onChange={e => {
                                            setComment(e.target.value);
                                        }}
                                        placeholder="Write a Comment"
                                        rows={1}
                                        className="mb-2"
                                    />
                                    <Button
                                        loading={isUpdateLoading}
                                        disabled={!comment}
                                        onClick={() => {
                                            updateLeadHandler({ isOnlyComment: true });
                                        }}
                                        type="primary"
                                    >
                                        Post
                                    </Button>
                                </div>
                            </Card>
                        </div>


                      <Card
                        title="Documents"
                        className="mb-4"
                        extra={
                          <Button
                            type="link" // Removes the outline, making it look like a link
                            onClick={downloadAllDocuments}
                            style={{
                              color: '#007AFF',
                              fontSize: '16px',
                              padding: 0,
                              display: 'flex',
                              flexDirection: 'row-reverse',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                            icon={
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M7.5 9.1665V14.1665L9.16667 12.4998"
                                  stroke="#007AFF"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M7.50016 14.1667L5.8335 12.5"
                                  stroke="#007AFF"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M18.3332 8.33317V12.4998C18.3332 16.6665 16.6665 18.3332 12.4998 18.3332H7.49984C3.33317 18.3332 1.6665 16.6665 1.6665 12.4998V7.49984C1.6665 3.33317 3.33317 1.6665 7.49984 1.6665H11.6665"
                                  stroke="#007AFF"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M18.3332 8.33317H14.9998C12.4998 8.33317 11.6665 7.49984 11.6665 4.99984V1.6665L18.3332 8.33317Z"
                                  stroke="#007AFF"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            }
                          >
                            Download All
                          </Button>
                        }
                      >
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: '16px',
                                    maxWidth: '100%',
                                    overflowX: 'hidden',
                                }}
                            >


                                <DocumentsUpload title="Profile Picture" maxFiles={3} uploadFiles={() => {}} uploadedFiles={documents.profile_pic} leadId={id ?? ''} isUploadAble={false} />
                                <DocumentsUpload title="CNIC" maxFiles={3} uploadFiles={() => {}} uploadedFiles={documents.cnic} leadId={id ?? ''} isUploadAble={false} />
                                <DocumentsUpload title="Bank Statement" maxFiles={3} uploadFiles={() => {}} uploadedFiles={documents.bank_statment} leadId={id ?? ''} isUploadAble={false} />
                                <DocumentsUpload title="Utility Bills" maxFiles={3} uploadFiles={() => {}} uploadedFiles={documents.bills} leadId={id ?? ''} isUploadAble={false} />
                                <DocumentsUpload title="Salary Slip" maxFiles={3} uploadFiles={() => {}} uploadedFiles={documents.salary_slip} leadId={id ?? ''} isUploadAble={false} />
                                <DocumentsUpload title="Cheque" maxFiles={3} uploadFiles={() => {}} uploadedFiles={documents.cheque} leadId={id ?? ''} isUploadAble={false} />
                                <div style={{ gridColumn: 'span 3' }}>
                                    <DocumentsUpload title="Other" maxFiles={11} uploadFiles={() => {}} uploadedFiles={documents.other} leadId={id ?? ''} isUploadAble={false} />
                                </div>
                            </div>

                        </Card>
                    </Content>
                }
            />
            {leadModalVisible ? (
                <Modal title="Lead Details" open={leadModalVisible} onCancel={onCloseHandler} footer={null} width="70%" className="rounded-lg">
                    <Tabs
                        destroyInactiveTabPane={true}
                        activeKey={activeTab}
                        defaultActiveKey="1"
                        items={tabItems}
                        onChange={handleTabChange}
                        className="custom-tabs"
                        renderTabBar={(props, DefaultTabBar) => (
                            <DefaultTabBar
                                {...props}
                                style={{
                                    borderBottom: '1px solid #DBD7D7',
                                }}
                            />
                        )}
                    ></Tabs>
                </Modal>
            ) : null}
        </>
    );
};

const DataRow = ({ title, value, index }: { title: string; value: string; index: number }) => {
    return (
        <div className="flex justify-between">
            <TextTypography classNames="font-normal" fontSize={16}>
                {title}
            </TextTypography>
            <div className="flex">
                <TextTypography classNames="font-medium " fontSize={16}>
                    {value}
                </TextTypography>
                {!(index % 2) ? <div className="w-[1px] h-[70%] bg-[#DBD7D7] mt-[5px] ml-2"></div> : null}
            </div>
        </div>
    );
};

export default LeadDetails;
